/**
 * Admin Analytics Manager
 * 
 * Provides focused admin-level analytics with consistent patterns and enhanced performance
 * Eliminates complex conditional logic and improves maintainability
 */

import { AnalyticsCalculator } from './analyticsCalculator';
import { bookingManager } from './bookingManager';
import { userManager } from './userManager';
import { spaceManager } from './spaceManager';
import type { 
  Booking, 
  Space, 
  Payment,
  CourtSportType
} from '@/lib/types';
import { format, subDays, parseISO, isValid } from 'date-fns';

export interface AnalyticsFilters {
  startDate?: string;
  endDate?: string;
  sportType?: CourtSportType | 'all-sports';
  spaceId?: string;
  userId?: string;
  organizationId?: string;
  clubId?: string;
}

export interface AnalyticsKPIData {
  utilization: { value: number; unit: string; change: number; changeType: 'positive' | 'negative' };
  bookings: { value: number; unit: string; change: number; changeType: 'positive' | 'negative' };
  users: { value: number; unit: string; change: number; changeType: 'positive' | 'negative' };
  revenue: { value: number; unit: string; change: number; changeType: 'positive' | 'negative' };
  busiestTime: { value: string; unit: string };
}

export interface ChartDataPoint {
  name: string;
  value: number;
  [key: string]: string | number;
}

export interface DurationData {
  name: string;
  value: number;
  percentage: number;
  color: string;
}

export interface TimeSeriesDataPoint extends Record<string, unknown> {
  name?: string;
  date?: string;
  day?: string;
  utilization?: number;
  bookings?: number;
  revenue?: number;
}

export interface UserAnalyticsData {
  name: string;
  bookings: number;
  hours: number;
  revenue: number;
  trend: number;
  trendType: 'positive' | 'negative';
}

export interface SpaceAnalyticsData {
  space: string;
  bookings: number;
  utilization: number;
  revenue: number;
  trend: number;
  trendType: 'positive' | 'negative';
}

export interface HeatmapData {
  day: string;
  hourlyUtilization: Array<{
    time: string;
    utilization: number;
  }>;
}

export interface AnalyticsResponse {
  kpiData: AnalyticsKPIData;
  dailyBookings: TimeSeriesDataPoint[];
  utilizationTimeline: TimeSeriesDataPoint[];
  utilizationByWeekday: ChartDataPoint[];
  durationBreakdown: DurationData[];
  topUsers: UserAnalyticsData[];
  topSpaces: SpaceAnalyticsData[];
  utilizationHeatmap: HeatmapData[];
  revenueTimeline: TimeSeriesDataPoint[];
}

/**
 * Simplified admin analytics manager with focused responsibility
 * Eliminates complex conditional logic and reduces cyclomatic complexity
 */
class AdminAnalyticsManager {
  private calculator = new AnalyticsCalculator();
  private cache: { data: AnalyticsResponse; timestamp: number; filters: string } | null = null;
  private readonly CACHE_DURATION = 60000; // 1 minute cache for analytics

  private isCacheValid(filters: AnalyticsFilters): boolean {
    if (!this.cache) return false;
    
    const currentFiltersKey = JSON.stringify(filters);
    const isExpired = (Date.now() - this.cache.timestamp) > this.CACHE_DURATION;
    const filtersChanged = this.cache.filters !== currentFiltersKey;
    
    return !isExpired && !filtersChanged;
  }

  /**
   * Get comprehensive analytics data with caching
   */
  async getAnalytics(filters: AnalyticsFilters = {}): Promise<AnalyticsResponse> {
    const filtersKey = JSON.stringify(filters);
    
    if (this.isCacheValid(filters)) {
      console.log('📊 [AdminAnalyticsManager] Returning cached analytics data');
      return JSON.parse(JSON.stringify(this.cache!.data));
    }

    try {
      console.log('📊 [AdminAnalyticsManager] Generating fresh analytics data with filters:', filters);
      
      // Get filtered data using simplified managers
      const { bookings, users, spaces, payments } = await this.getFilteredData(filters);
      
      console.log('📊 [AdminAnalyticsManager] Data summary:', {
        bookingsCount: bookings.length,
        usersCount: users.length,
        spacesCount: spaces.length,
        paymentsCount: payments.length
      });

      // Use calculator for all computations
      const calculatorData = { bookings, users, spaces, payments };
      const kpis = this.calculator.calculateKPIs(calculatorData);
      
      // Convert KPI format to match expected interface
      const kpiData: AnalyticsKPIData = {
        utilization: { value: kpis.utilization, unit: '%', change: 0, changeType: 'positive' },
        bookings: { value: kpis.bookingCount, unit: 'bookings', change: 0, changeType: 'positive' },
        users: { value: kpis.activeUsers, unit: 'users', change: 0, changeType: 'positive' },
        revenue: { value: kpis.revenue, unit: 'USD', change: 0, changeType: 'positive' },
        busiestTime: { value: kpis.busiestTime, unit: '' }
      };

      const dailyBookings = this.calculator.generateDailyBookings(bookings);
      const utilizationByWeekday = this.calculator.generateWeekdayUtilization(bookings, spaces);
      const durationBreakdown = this.calculator.generateDurationBreakdown(bookings);
      const topUsers = this.calculator.generateTopUsers(bookings, users, payments);
      const topSpaces = this.calculator.generateTopSpaces(bookings, spaces);

      // Generate additional analytics data
      const utilizationHeatmap = this.generateHeatmapData(bookings, spaces);
      const revenueTimeline = this.generateRevenueTimeline(bookings, payments);

      const analyticsData: AnalyticsResponse = {
        kpiData,
        dailyBookings: dailyBookings.map(d => ({ name: d.name, bookings: d.value })),
        utilizationTimeline: dailyBookings.map(d => ({ name: d.name, utilization: d.value })),
        utilizationByWeekday,
        durationBreakdown: durationBreakdown.map(d => ({ ...d, color: 'hsl(var(--chart-1))' })),
        topUsers: topUsers.map(u => ({ ...u, trend: 0, trendType: 'positive' as const })),
        topSpaces: topSpaces.map(s => ({ ...s, trend: 0, trendType: 'positive' as const })),
        utilizationHeatmap,
        revenueTimeline
      };
      
      console.log('📊 [AdminAnalyticsManager] Generated analytics data:', {
        kpiData: kpiData,
        chartDataCounts: {
          dailyBookings: analyticsData.dailyBookings.length,
          utilizationByWeekday: analyticsData.utilizationByWeekday.length,
          durationBreakdown: analyticsData.durationBreakdown.length,
          topUsers: analyticsData.topUsers.length,
          topSpaces: analyticsData.topSpaces.length,
          utilizationHeatmap: analyticsData.utilizationHeatmap.length,
          revenueTimeline: analyticsData.revenueTimeline.length
        }
      });

      // Update cache
      this.cache = {
        data: JSON.parse(JSON.stringify(analyticsData)),
        timestamp: Date.now(),
        filters: filtersKey
      };

      return analyticsData;
    } catch (error) {
      console.error('Error calculating analytics:', error);
      return this.getFallbackAnalytics();
    }
  }

  /**
   * Get KPI data only (faster endpoint)
   */
  async getKPIs(filters: AnalyticsFilters = {}): Promise<AnalyticsKPIData> {
    try {
      const { bookings, users, spaces, payments } = await this.getFilteredData(filters);
      const calculatorData = { bookings, users, spaces, payments };
      const kpis = this.calculator.calculateKPIs(calculatorData);
      
      return {
        utilization: { value: kpis.utilization, unit: '%', change: 0, changeType: 'positive' },
        bookings: { value: kpis.bookingCount, unit: 'bookings', change: 0, changeType: 'positive' },
        users: { value: kpis.activeUsers, unit: 'users', change: 0, changeType: 'positive' },
        revenue: { value: kpis.revenue, unit: 'USD', change: 0, changeType: 'positive' },
        busiestTime: { value: kpis.busiestTime, unit: '' }
      };
    } catch (error) {
      console.error('Error calculating KPIs:', error);
      return this.getFallbackKPIs();
    }
  }

  /**
   * Get chart data for specific chart type
   */
  async getChartData(
    chartType: string, 
    filters: AnalyticsFilters = {}
  ): Promise<TimeSeriesDataPoint[] | ChartDataPoint[] | DurationData[] | UserAnalyticsData[] | SpaceAnalyticsData[] | HeatmapData[]> {
    try {
      const { bookings, users, spaces, payments } = await this.getFilteredData(filters);

      switch (chartType) {
        case 'daily-bookings':
          return this.calculator.generateDailyBookings(bookings);
        case 'utilization-timeline':
          return this.calculator.generateDailyBookings(bookings);
        case 'utilization-weekday':
          return this.calculator.generateWeekdayUtilization(bookings, spaces);
        case 'duration-breakdown':
          return this.calculator.generateDurationBreakdown(bookings);
        case 'top-users':
          return this.calculator.generateTopUsers(bookings, users, payments);
        case 'top-spaces':
          return this.calculator.generateTopSpaces(bookings, spaces);
        case 'utilization-heatmap':
          return this.generateHeatmapData(bookings, spaces);
        case 'revenue-timeline':
          return this.generateRevenueTimeline(bookings, payments);
        default:
          throw new Error(`Unknown chart type: ${chartType}`);
      }
    } catch (error) {
      console.error(`Error generating chart data for ${chartType}:`, error);
      return [];
    }
  }

  /**
   * Get filtered data using simplified managers
   */
  private async getFilteredData(filters: AnalyticsFilters) {
    console.log('🔍 [AdminAnalyticsManager] Fetching all data from managers...');
    
    const [allBookings, allUsers, allSpaces] = await Promise.all([
      bookingManager.getAllBookings(),
      userManager.getAllUsers(),
      spaceManager.getAllSpaces()
    ]);
    
    console.log('🔍 [AdminAnalyticsManager] Raw data counts:', {
      allBookings: allBookings.length,
      allUsers: allUsers.length,
      allSpaces: allSpaces.length
    });

    // Filter bookings based on criteria
    const bookings = allBookings.filter(booking => {
      // Date range filter
      if (filters.startDate && filters.endDate) {
        const bookingDate = parseISO(booking.date);
        const start = parseISO(filters.startDate);
        const end = parseISO(filters.endDate);
        if (!isValid(bookingDate) || bookingDate < start || bookingDate > end) {
          return false;
        }
      }

      // Sport type filter
      if (filters.sportType && filters.sportType !== 'all-sports') {
        if (booking.sport !== filters.sportType) {
          return false;
        }
      }

      // Space filter
      if (filters.spaceId && booking.spaceId !== filters.spaceId) {
        return false;
      }

      // User filter
      if (filters.userId && booking.userUid !== filters.userId) {
        return false;
      }

      // Organization filter
      if (filters.organizationId && booking.organizationId !== filters.organizationId) {
        return false;
      }

      // Club filter
      if (filters.clubId && booking.clubId !== filters.clubId) {
        return false;
      }

      return true;
    });

    // Filter related data
    const relevantUserIds = new Set(bookings.map(b => b.userUid));
    const relevantSpaceIds = new Set(bookings.map(b => b.spaceId));
    
    const users = allUsers.filter(user => relevantUserIds.has(user.uid));
    const spaces = allSpaces.filter(space => relevantSpaceIds.has(space.id));
    
    // Generate sample payments based on bookings
    const payments = this.generateSamplePayments(bookings);
    
    console.log('🔍 [AdminAnalyticsManager] Filtered data counts:', {
      filteredBookings: bookings.length,
      relevantUsers: users.length,
      relevantSpaces: spaces.length,
      generatedPayments: payments.length
    });

    return { bookings, users, spaces, payments };
  }

  /**
   * Generate heatmap data for utilization by time and day
   */
  private generateHeatmapData(bookings: Booking[], spaces: Space[]): HeatmapData[] {
    const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const timeSlots = Array.from({ length: 12 }, (_, i) => `${8 + i}:00`);
    
    return weekdays.map((day, dayIndex) => ({
      day,
      hourlyUtilization: timeSlots.map(timeSlot => {
        const hour = parseInt(timeSlot);
        const relevantBookings = bookings.filter(booking => {
          const bookingDate = parseISO(booking.date);
          const bookingDay = isValid(bookingDate) ? bookingDate.getDay() : -1;
          const adjustedDay = bookingDay === 0 ? 6 : bookingDay - 1; // Convert Sunday=0 to 6, Monday=1 to 0, etc.
          
          const startHour = parseInt(booking.startTime.split(':')[0]);
          return adjustedDay === dayIndex && startHour === hour;
        });
        
        const utilization = spaces.length > 0 ? (relevantBookings.length / spaces.length) * 100 : 0;
        
        return {
          time: timeSlot,
          utilization: Math.round(utilization)
        };
      })
    }));
  }

  /**
   * Generate revenue timeline data
   */
  private generateRevenueTimeline(bookings: Booking[], payments: Payment[]): TimeSeriesDataPoint[] {
    const dates = Array.from({ length: 7 }, (_, i) => subDays(new Date(), 6 - i));
    
    return dates.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayPayments = payments.filter(p => {
        const paymentDate = parseISO(p.date);
        return isValid(paymentDate) && format(paymentDate, 'yyyy-MM-dd') === dateStr && p.status === 'Paid';
      });
      
      const revenue = dayPayments.reduce((sum, payment) => sum + payment.amount, 0);
      
      return {
        name: format(date, 'MMM d'),
        revenue: Math.round(revenue)
      };
    });
  }

  /**
   * Generate sample payments based on bookings
   */
  private generateSamplePayments(bookings: Booking[]): Payment[] {
    return bookings.map(booking => ({
      id: `payment_${booking.id}`,
      userUid: booking.userUid,
      amount: booking.priceAmount || 25,
      currency: booking.currency || 'USD',
      currencyCode: booking.currency || 'USD',
      status: 'Paid' as const,
      date: booking.dateCreated || booking.date,
      description: `Payment for booking ${booking.id}`,
      subscriptionId: '',
      organizationId: booking.organizationId,
      tenantId: booking.tenantId
    }));
  }

  /**
   * Get fallback analytics data when errors occur
   */
  private getFallbackAnalytics(): AnalyticsResponse {
    return {
      kpiData: this.getFallbackKPIs(),
      dailyBookings: [],
      utilizationTimeline: [],
      utilizationByWeekday: [],
      durationBreakdown: [],
      topUsers: [],
      topSpaces: [],
      utilizationHeatmap: [],
      revenueTimeline: []
    };
  }

  /**
   * Get fallback KPI data when errors occur
   */
  private getFallbackKPIs(): AnalyticsKPIData {
    return {
      utilization: { value: 0, unit: '%', change: 0, changeType: 'positive' },
      bookings: { value: 0, unit: 'bookings', change: 0, changeType: 'positive' },
      users: { value: 0, unit: 'users', change: 0, changeType: 'positive' },
      revenue: { value: 0, unit: 'USD', change: 0, changeType: 'positive' },
      busiestTime: { value: 'No data', unit: '' }
    };
  }
}

export const adminAnalyticsManager = new AdminAnalyticsManager(); 
